import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, Loader2, Building2, Tag, Sparkles } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { directoryApi, membersApi } from "@/lib/api/directory";
import { iconForCategory } from "@/lib/categoryIcons";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

function OfferTile({ offer, lang }) {
    const title = lang === "ar" ? (offer.title_ar || offer.title_en) : (offer.title_en || offer.title_ar);
    const isPercent = offer.discount_type === "PERCENTAGE";
    return (
        <Link
            to={`/deal/${offer.id}`}
            className="group rounded-xl border border-border bg-card overflow-hidden hover:border-primary/40 hover:shadow-md transition-all flex"
            data-testid={`recommended-offer-${offer.id}`}
        >
            <div className="w-24 h-24 shrink-0 bg-secondary flex items-center justify-center overflow-hidden">
                {offer.image_url
                    ? <img src={offer.image_url} alt={title} className="w-full h-full object-cover" />
                    : <Sparkles className="h-7 w-7 text-primary/40" />}
            </div>
            <div className="flex-1 min-w-0 p-3 flex flex-col justify-center">
                <p className="text-xs text-muted-foreground truncate">{offer.provider_name}</p>
                <p className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">
                    {title}
                </p>
                <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className="font-bold text-primary" dir="ltr">
                        {isPercent
                            ? `${Math.round(offer.discount_value)}% OFF`
                            : `${Math.round(offer.discount_value)} EGP`}
                    </span>
                </div>
            </div>
        </Link>
    );
}

function useDebounce(value, delay = 250) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

function L1Card({ item, lang }) {
    const Icon = iconForCategory(item.name_en);
    const primary = lang === "ar" ? item.name_ar : item.name_en;
    const secondary = lang === "ar" ? item.name_en : item.name_ar;
    return (
        <Link
            to={`/member/category/${item.id}`}
            className="group surface-card p-5 hover:border-primary/40 hover:shadow-md transition-all flex flex-col gap-3"
            data-testid={`directory-l1-${item.id}`}
        >
            <div className="flex items-start justify-between">
                <div className="icon-tile h-12 w-12">
                    <Icon className="h-6 w-6" />
                </div>
                <Badge variant="outline" className="text-[11px]">
                    {item.children_count}
                </Badge>
            </div>
            <div className="min-w-0">
                <h3 className="text-base font-semibold text-foreground truncate">{primary}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground truncate" lang={lang === "ar" ? "en" : "ar"}>
                    {secondary}
                </p>
                {lang === "ar" ? (
                    item.explanatory_name_ar && (
                        <p className="mt-2 text-xs text-muted-foreground line-clamp-2 font-arabic" dir="rtl">
                            {item.explanatory_name_ar}
                        </p>
                    )
                ) : (
                    item.explanatory_name_en && (
                        <p className="mt-2 text-xs text-muted-foreground line-clamp-2">
                            {item.explanatory_name_en}
                        </p>
                    )
                )}
            </div>
            <div className="mt-auto pt-2 flex items-center text-primary text-xs font-medium">
                <span>{lang === "ar" ? "تصفح" : "Browse"}</span>
                <ChevronRight className="h-3.5 w-3.5 ms-1 group-hover:translate-x-0.5 transition-transform" />
            </div>
        </Link>
    );
}

function SearchResultCard({ item, lang, kind }) {
    const isProvider = kind === "provider";
    const primary = isProvider
        ? item.corporate_name
        : (lang === "ar" ? item.name_ar : item.name_en);
    const secondary = isProvider
        ? (item.neighborhoods || []).join(" · ")
        : (lang === "ar" ? item.name_en : item.name_ar);
    const to = isProvider ? `/member/provider/${item.id}` : `/member/category/${item.id}`;
    const Icon = isProvider ? Building2 : Tag;
    return (
        <Link
            to={to}
            className="rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/40 transition-colors flex items-center gap-3"
            data-testid={`directory-search-${kind}-${item.id}`}
        >
            <div className="icon-tile h-10 w-10 shrink-0">
                <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{primary}</p>
                <p className="text-xs text-muted-foreground truncate">{secondary}</p>
                {!isProvider && item.parents && item.parents.length > 0 && (
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                        {(item.parents || []).map((p) => (lang === "ar" ? p.name_ar : p.name_en)).join(", ")}
                    </p>
                )}
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Link>
    );
}

export default function MemberHomePage() {
    const { t, lang, isRTL } = useI18n();
    const [items, setItems] = useState(null);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [recommended, setRecommended] = useState(null);
    const [recommendedMatched, setRecommendedMatched] = useState(false);
    const debounced = useDebounce(query, 250);

    const load = useCallback(async () => {
        try {
            const list = await directoryApi.topCategories();
            setItems(list);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let active = true;
        // First try interests-matched offers; fall back to a generic "latest" list.
        membersApi.listOffers({ matched_only: true, limit: 6 })
            .then((res) => {
                if (!active) return;
                if (res.items && res.items.length > 0) {
                    setRecommended(res.items);
                    setRecommendedMatched(true);
                } else {
                    return membersApi.listOffers({ limit: 6 }).then((fallback) => {
                        if (!active) return;
                        setRecommended(fallback.items || []);
                        setRecommendedMatched(false);
                    });
                }
            })
            .catch(() => { if (active) setRecommended([]); });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        let active = true;
        const q = debounced.trim();
        if (!q) { setResults(null); return; }
        setSearching(true);
        directoryApi.search(q)
            .then((d) => { if (active) setResults(d); })
            .catch((err) => active && toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message))
            .finally(() => { if (active) setSearching(false); });
        return () => { active = false; };
    }, [debounced]);

    return (
        <div className="max-w-6xl space-y-8 fade-up" data-testid="page-member-home">
            <header>
                <p className="text-sm font-medium text-primary">{t("member.home.eyebrow")}</p>
                <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight">
                    {t("member.home.title")}
                </h1>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl">
                    {t("member.home.subtitle")}
                </p>
            </header>

            <div className="surface-card p-2" dir={isRTL ? "rtl" : "ltr"}>
                <div className="relative">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("member.home.searchPlaceholder")}
                        className="ps-11 pe-3 h-14 border-0 shadow-none focus-visible:ring-0 text-base"
                        data-testid="member-search-input"
                    />
                    {searching && <Loader2 className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
                </div>
            </div>

            {!debounced.trim() && items === null && (
                <div className="surface-card p-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            )}

            {!debounced.trim() && recommended && recommended.length > 0 && (
                <section data-testid="member-home-recommended">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-semibold text-foreground">
                            {recommendedMatched
                                ? (lang === "ar" ? "موصى به لك" : "Recommended for you")
                                : (lang === "ar" ? "أحدث العروض" : "Latest offers")}
                        </h2>
                        <Link
                            to="/search"
                            className="text-xs text-primary font-medium hover:underline inline-flex items-center"
                        >
                            {lang === "ar" ? "عرض الكل" : "See all"}
                            <ChevronRight className="h-3.5 w-3.5 ms-1" />
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {recommended.map((offer) => (
                            <OfferTile key={offer.id} offer={offer} lang={lang} />
                        ))}
                    </div>
                </section>
            )}

            {!debounced.trim() && items && (
                <section>
                    <h2 className="text-base font-semibold text-foreground mb-4">
                        {t("member.home.exploreSpecialties")}
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((item) => (
                            <L1Card key={item.id} item={item} lang={lang} />
                        ))}
                    </div>
                </section>
            )}

            {debounced.trim() && results && (
                <section className="space-y-5" data-testid="member-search-results">
                    <p className="text-sm text-muted-foreground">
                        {t("member.home.searchResultsHeader", {
                            cats: results.items?.length || 0,
                            provs: results.providers?.length || 0,
                            q: debounced,
                        })}
                    </p>

                    {results.providers?.length > 0 && (
                        <div>
                            <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                                {t("member.home.providersSection")}
                            </h3>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {results.providers.map((p) => (
                                    <SearchResultCard key={`p-${p.id}`} item={p} lang={lang} kind="provider" />
                                ))}
                            </div>
                        </div>
                    )}

                    {results.items?.length > 0 && (
                        <div>
                            <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                                {t("member.home.specialtiesSection")}
                            </h3>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {results.items.map((c) => (
                                    <SearchResultCard key={`c-${c.id}`} item={c} lang={lang} kind="category" />
                                ))}
                            </div>
                        </div>
                    )}

                    {results.providers?.length === 0 && results.items?.length === 0 && (
                        <div className="surface-card p-10 text-center">
                            <p className="text-sm text-muted-foreground">{t("member.home.noResults")}</p>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
